import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Text } from '../../../../components/Text';
import { Select } from '../../../../components/Select';
import { Textarea } from '../../../../components/Textarea';
import { Button } from '../../../../components/Button';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import Loading from '../../../../components/Loading';
import { SetupBrand } from '../../../../models/SetupBrand';
import { useSetupBrand } from '../../../../hooks/useSetupBrand';
import { useWarehouses } from '../../../../hooks/useWarehouse';

const SetupEditBrandPage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>(); // Assuming `id` is passed as a URL parameter
    const [brand, setBrand] = useState<SetupBrand | null>(null);
    const { updateBrand, fetchBrandById, loading, error } = useSetupBrand();

    useEffect(() => {
        document.title = "Edit Brand";
    }, []);

    useEffect(() => {
        const fetchBrandDetails = async () => {
            if (!id) return; // Ensure ID is valid before making the request

            try {
                const fetchedBrand = await fetchBrandById(Number(id));
                setBrand(fetchedBrand);
            } catch (err) {
                showErrorToast('Failed to fetch brand details.');
            }
        };
        fetchBrandDetails();
    }, [id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setBrand(prev => prev ? ({ ...prev, [name]: value }) : null);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!brand) return;

        try {
            const res = await updateBrand(Number(brand.id!), brand);
            showLoadingToast(`Updating ${brand.name}...`, 100);
            if (res.dbsuccess) {
                setTimeout(() => {
                    navigate('/gp/inventories/brands');
                    showSuccessToast(`${brand.name} updated successfully`);
                }, 1000);
            } else {
                showErrorToast(`Failed updating brand data`);
            }
        } catch (err) {
            showErrorToast(`Failed updating brand data: ${err}`);
        }
    };

    if (loading) return <Loading />;
    if (error) return <p>{error}</p>;
    if (!brand) return <p>Brand not found.</p>;

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Edit Brand</Heading>
                <div className="mt-5 flex lg:ml-4 lg:mt-0">
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className="ml-3">Save</Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Brand</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Differentiates a company’s products or services.
                        </p>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Brand Name</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Name"
                                        name="name"
                                        value={brand.name!}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </Card>

            <div className="flex justify-end gap-4 my-6">
                <Button type="reset" plain onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button type="submit">Save</Button>
            </div>
        </form>
    );
};

export default SetupEditBrandPage;
